import React, { useEffect, useState, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, Flex, Stack, Text } from '../../../vanilla'
import { LockIcon } from '../../../components/icons'
import LoadingOverlay from '../../../components/loading-overlay'
import { useGlobalStore, useOrderStore } from '../../../store/hooks/useStore'
import { FetchPaymentMethodsPayload } from 'braintree-web'
import { useCheckoutState } from '../../../contexts'
import { CheckoutBonusCardStatus, FilteredPaymentMethod } from '../../../types/store/common'
import {CHECKOUT_ERROR_MESSAGES } from '../../../utils/constants'
import { AvailablePaymentMethodsIcons } from '../../../components/credit-card-type-icon'
import { UpdatePaymentAmount } from './update-payment-amount'
import { PaymentOptions } from './payment-options'
import { BraintreeInputsProps } from '../../../types/braintree'

interface PaymentSectionProps {
  submitOrderProcess: () => Promise<void | null>
  userVaultedCardsLoaded: boolean
  bonusCardStatus: CheckoutBonusCardStatus
  errorMessage: string | null
  userVaultedCards: FetchPaymentMethodsPayload[]
  selectedPayment: string | null
  setSelectedPayment: React.Dispatch<React.SetStateAction<string | null>>
  usingSavedCard: boolean
  braintreeInputs: BraintreeInputsProps
  hostedFieldsValidationErrors: {
    cardholderName: boolean
    number: boolean
    expirationDate: boolean
    cvv: boolean
  }
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>
  braintreeFormIsValid: boolean
  hostedFieldsErrors: boolean
  threeDSError: boolean
  isTopUpPage: boolean
  paymentMethods: FilteredPaymentMethod[]
}

export const PaymentSection = observer((props: PaymentSectionProps) => {
  const {
    submitOrderProcess,
    userVaultedCardsLoaded,
    bonusCardStatus,
    errorMessage,
    userVaultedCards,
    selectedPayment,
    setSelectedPayment,
    usingSavedCard,
    braintreeInputs,
    hostedFieldsValidationErrors,
    braintreeFormIsValid,
    hostedFieldsErrors,
    threeDSError,
    isTopUpPage,
    paymentMethods
  } = props

  const {
    customSitePreferences,
    isCustomSitePreferenceSet,
    getCustomSitePreferenceById,
  } = useGlobalStore()

  const { isPaymentRefunding, isPaymentRequired } = useOrderStore()

  const { setSaveCreditCard } = useCheckoutState()
  const [savedCardEnabled, setSavedCardEnabled] = useState<boolean>(false)
  const [allowSaveCreditCard, setAllowSaveCreditCard] = useState<boolean>(false)
 

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({
    cardNumber: null,
    cardName: null,
    cardExpiry: null,
    cardCvv: null,
  })

  useEffect(() => {
    const handleAutoFill = (event: AnimationEvent) => {
      const target = event.target as HTMLInputElement
      target.dispatchEvent(new Event('input', { bubbles: true }))
    }

    const inputs = Object.values(inputRefs.current)
    inputs.forEach((input) => {
      input?.addEventListener('animationstart', handleAutoFill)
    })

    return () => {
      inputs.forEach((input) => {
        input?.removeEventListener('animationstart', handleAutoFill)
      })
    }
  }, [])

  // Checks custom pref for allowing saved cards
  useEffect(() => {
     (async function () {
      if (!isCustomSitePreferenceSet('BRAINTREE_savedCardEnabled')) {
        await getCustomSitePreferenceById('BRAINTREE_savedCardEnabled')
      }
      setSavedCardEnabled(customSitePreferences['BRAINTREE_savedCardEnabled'] as boolean)
    })()
  })

  // Checks custom pref for Braintree Vault Mode
  useEffect(() => {
     (async function () {
      if (!isCustomSitePreferenceSet('BRAINTREE_Vault_Mode')) {
        await getCustomSitePreferenceById('BRAINTREE_Vault_Mode')
      }
      setAllowSaveCreditCard(customSitePreferences['BRAINTREE_Vault_Mode'] as boolean)
    })()
  })

  let showErrorHeading

  if (isTopUpPage) {
    showErrorHeading = errorMessage && errorMessage === CHECKOUT_ERROR_MESSAGES.BONUSCARD_ERROR_GENERAL
  } else {
    showErrorHeading = errorMessage && errorMessage === CHECKOUT_ERROR_MESSAGES.ERROR_GENERAL
  }
  return (
    <Stack
      borderRadius={[null, null, 'base']}
      position="relative"
      bg="white"
      boxShadow="base"
      gap="0px"
    >
      {threeDSError && (
        <Box border="2px" borderColor="accent0" padding={['16px', '16px', '16px']}>
          <Text variant="text3">{CHECKOUT_ERROR_MESSAGES.THREEDSECURE_CANCELLED}</Text>
        </Box>
      )}
      <Box borderBottom="1px" borderColor="border1" padding={['16px', '16px', '16px']}>
        <Flex flexDirection={['column', 'row']} justify="space-between" alignItems={['flex-start', 'center']}>
          <Stack alignItems="baseline" direction="row">
            <LockIcon />
            <Text variant="heading1">{isTopUpPage ? 'Top up' : 'Payment'} Method</Text>
          </Stack>
          {!isTopUpPage && (
            <Stack direction="row" alignItems='center' paddingTop={['10px', '0px']}>
              <AvailablePaymentMethodsIcons availablePaymentMethods={paymentMethods} isFooter={false} />
            </Stack>
          )}
        </Flex>
      </Box>
      <LoadingOverlay
        isLoading={
          (!userVaultedCardsLoaded) &&
          !bonusCardStatus?.hidePayment &&
          !isPaymentRefunding &&
          isPaymentRequired
        }
      />
      {errorMessage && errorMessage !== CHECKOUT_ERROR_MESSAGES.CVV_ERROR && (
        <Stack
          paddingX={['16px', '20px']}
          paddingY={['20px', '24px']}
          style={{ background: '#F9E5E9' }}
        >
          {showErrorHeading && (
            <Text variant="heading1" lineHeight="shorter" color="accent0">
              Unable to {isTopUpPage ? 'top up your Bonus Card' : 'place order'}
            </Text>
          )}
          <Text variant="text4" lineHeight="shorter" color="accent0">
            {errorMessage}
          </Text>
        </Stack>
      )}

      {(!isPaymentRequired || bonusCardStatus?.hidePayment) && !isTopUpPage ?
        <UpdatePaymentAmount submitOrderProcess={submitOrderProcess} />
        : <PaymentOptions
          allowSaveCreditCard={allowSaveCreditCard}
          bonusCardStatus={bonusCardStatus}
          braintreeFormIsValid={braintreeFormIsValid}
          braintreeInputs={braintreeInputs}
          errorMessage={errorMessage}
          hostedFieldsErrors={hostedFieldsErrors}
          hostedFieldsValidationErrors={hostedFieldsValidationErrors}
          isTopUp={isTopUpPage}
          inputRefs={inputRefs}
          savedCardEnabled={savedCardEnabled}
          selectedPayment={selectedPayment}
          setSaveCreditCard={setSaveCreditCard}
          submitOrderProcess={submitOrderProcess}
          usingSavedCard={usingSavedCard}
          setSelectedPayment={setSelectedPayment}
          userVaultedCards={userVaultedCards}
          paymentMethods={paymentMethods}
        />
      }
    </Stack>
  )
})
